import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import BackPage from "../../components/button/BackPage";
import RegularContainer from "../../components/container/RegularContainer";
import InnerTabs from "../../components/tab/InnerTabs";
import { useGetProjectQuery } from "../../features/projects/services";
import { ProjectVersion } from "../../features/projects/types/interfaces";
import { ILinkTabProps } from "../../types/interfaces";

const allTabRoutes: ILinkTabProps[] = [
    // v2的知識庫全搜索功能API未實作，因此根據專案版本動態隱藏該路由
    { labelKey: "KNOWLEDGE.SearchAllRecordTab", href: "search-all-record" },
    { labelKey: "KNOWLEDGE.Knowledge_inuse", href: "inuse-knowledge" },
    { labelKey: "KNOWLEDGE.Unused_Knowledge", href: "mine-knowledge" },
    // { labelKey: "KNOWLEDGE.Public_Knowledge", href: "public-knowledge" },
    //hide temporarily
    { labelKey: "KNOWLEDGE._Knowledge_Package_Import", href: "export-import-knowledge" },
    { labelKey: "KNOWLEDGE.Create_Knowledge", href: "create-knowledge" },
];

const KnowledgeTabs = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { state } = location;
    const { projectId } = useParams();

    const [currentTab, setCurrentTab] = useState(0);

    // 獲取專案資料以檢查版本
    const { data: projectData } = useGetProjectQuery({ id: projectId }, { skip: !projectId });

    /**
     * 根據專案版本動態過濾標籤頁
     * V1 專案：顯示全部標籤頁（包含全內文檢索、Excel匯入匯出）
     * V2 專案：隱藏全內文檢索標籤頁 --> 等待API實作後再顯示 
     * 隱藏Excel匯入匯出標籤頁 --> V2使用文件段落結構，不適用V1的Excel格式
     */
    const tabRoutes = useMemo(() => {
        const projectVersion = projectData?.data?.version;

        // 如果是 V2 專案，過濾掉不支援的標籤頁
        if (projectVersion === ProjectVersion.V2) {
            const hiddenTabsForV2 = ["search-all-record", "export-import-knowledge"];
            return allTabRoutes.filter((tab) => !hiddenTabsForV2.includes(tab.href));
        }

        // V1 專案或沒有版本資訊時，顯示全部標籤頁
        return allTabRoutes;
    }, [projectData]);

    const handleChange = (newValue: number) => {
        setCurrentTab(newValue);
    };

    const pathnameSplit = location?.pathname?.split("/");
    const currentRoute = pathnameSplit[pathnameSplit?.length - 1] || "";

    const findIdx = tabRoutes.findIndex((r) => {
        return r.href === currentRoute;
    });

    const backUrl = state ? `/projects/${state}` : `/projects/${projectId}`;

    useEffect(() => {
        if (findIdx) {
            setCurrentTab(findIdx);
            return;
        }
        setCurrentTab(0);
    }, [findIdx, currentRoute]);

    return (
        <>
            <BackPage path={backUrl} />
            <RegularContainer sx={{ pb: 0 }}>
                <RegularContainer.Heading title={t("KNOWLEDGE.Manage_Knowledge") || ""} />
                <RegularContainer.Content>
                    <InnerTabs
                        currentTab={currentTab}
                        handleChange={handleChange}
                        tabRoutes={tabRoutes}
                        tabsOnClick={(tabRoute) => {
                            navigate(`/projects/${projectId}/knowledge/${tabRoute.href}`, {
                                state: projectId,
                            });
                        }}
                    />
                </RegularContainer.Content>
            </RegularContainer>
        </>
    );
};

export default KnowledgeTabs;
